// src/pages/RecommandationsPage/RecommandationsPage.tsx
import React from 'react';
import Header from '../../components/Header/Header';
import Footer from '../../components/Footer/Footer';
import Navigation from '../../components/Navigation/Navigation';
import recommandationsEpreuveData from '../../data/recommandations-epreuve.json';
import './RecommandationsPage.css';
import SEO from '../../components/SEO/SEO';

interface RecommendationItem {
  number: number;
  text: string;
  subItems?: string[];
}

interface ContentItem {
  type: string;
  text?: string;
  items?: RecommendationItem[];
  mainMessage?: string;
  title?: string;
  content?: string;
  sources?: Array<{
    type: string;
    label: string;
    text: string;
    url: string;
  }>;
}

interface RecommandationsData {
  title: string;
  author: string;
  description: string;
  content: ContentItem[];
}

const RecommandationsPage: React.FC = () => {
  const data = recommandationsEpreuveData as RecommandationsData;

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const renderContentItem = (item: ContentItem, index: number) => {
    switch (item.type) {
      case 'subtitle':
        return <h2 key={index} className="recommandations-subtitle">{item.text}</h2>;
      
      case 'section-title':
        return <h3 key={index} className="recommandations-section-title">{item.text}</h3>;
      
      case 'paragraph':
        return (
          <div key={index} className="recommandations-paragraph">
            {item.text?.split('\n').map((line, lineIndex) => (
              <p key={lineIndex}>{line}</p>
            ))}
          </div>
        );
      
      case 'highlight':
        return (
          <div key={index} className="recommandations-highlight">
            {item.text?.split('\n').map((line, lineIndex) => (
              <p key={lineIndex}>{line}</p>
            ))}
          </div>
        );
      
      case 'recommendations-list':
        return (
          <div key={index} className="recommandations-list-container">
            {item.items?.map((recItem, recIndex) => (
              <div key={recIndex} className="recommandations-item">
                <div className="recommandations-item-header">
                  <div className="recommandations-item-number">{recItem.number}</div>
                  <div className="recommandations-item-text">{recItem.text}</div>
                </div>
                {recItem.subItems && recItem.subItems.length > 0 && (
                  <div className="recommandations-item-subitems">
                    {recItem.subItems.map((subItem, subIndex) => (
                      <div key={subIndex} className="recommandations-subitem">
                        <span className="recommandations-bullet">•</span>
                        <span>{subItem}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        );
      
      case 'separator':
        return <hr key={index} className="recommandations-separator" />;
      
      case 'conclusion-card':
        return (
          <div key={index} className="recommandations-conclusion-card">
            <div className="recommandations-conclusion-header">
              <div className="recommandations-conclusion-icon">🤲</div>
              <h3 className="recommandations-conclusion-title">{item.title}</h3>
            </div>
            <div className="recommandations-conclusion-main">
              <p className="recommandations-conclusion-message">{item.mainMessage}</p>
            </div>
            <div className="recommandations-conclusion-content">
              {item.content?.split('\n\n').map((paragraph, pIndex) => (
                <p key={pIndex}>{paragraph}</p>
              ))}
            </div>
          </div>
        );
      
      case 'sources-card':
        return (
          <div key={index} className="recommandations-sources-card">
            <div className="recommandations-sources-header">
              <div className="recommandations-sources-icon">📚</div>
              <h3 className="recommandations-sources-title">{item.title}</h3>
            </div>
            <div className="recommandations-sources-content">
              {item.sources?.map((source, sourceIndex) => {
                const getYouTubeVideoId = (url: string) => {
                  const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/);
                  return match ? match[1] : null;
                };
                
                const videoId = source.type === 'video' ? getYouTubeVideoId(source.url) : null;
                
                if (source.type === 'video' && videoId) {
                  return (
                    <div key={sourceIndex} className="recommandations-source-item recommandations-source-video">
                      <div className="recommandations-source-header">
                        <div className="recommandations-source-icon">
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M23.498 6.186a2.97 2.97 0 0 0-2.089-2.089C19.636 3.698 12 3.698 12 3.698s-7.636 0-9.409.399A2.97 2.97 0 0 0 .502 6.186C.103 7.96.103 12 .103 12s0 4.04.399 5.814a2.97 2.97 0 0 0 2.089 2.089C4.364 20.302 12 20.302 12 20.302s7.636 0 9.409-.399a2.97 2.97 0 0 0 2.089-2.089C23.897 16.04 23.897 12 23.897 12s0-4.04-.399-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" fill="#FF0000"/>
                          </svg>
                        </div>
                        <div className="recommandations-source-text-info">
                          <div className="recommandations-source-label">{source.label}</div>
                          <div className="recommandations-source-text">{source.text}</div>
                        </div>
                      </div>
                      <div className="recommandations-video-thumbnail-container">
                        <a 
                          href={source.url} 
                          target="_blank" 
                          rel="noopener noreferrer" 
                          className="recommandations-video-thumbnail-link"
                        >
                          <div className="recommandations-video-thumbnail">
                            <img 
                              src={`https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`}
                              alt="Vignette de la vidéo YouTube"
                              onError={(e) => {
                                (e.target as HTMLImageElement).src = `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
                              }}
                            />
                            <div className="recommandations-video-play-overlay">
                              <div className="recommandations-video-play-button">
                                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M8 5v14l11-7z" fill="white"/>
                                </svg>
                              </div>
                            </div>
                          </div>
                          <div className="recommandations-video-title">
                            Regarder la vidéo
                            <span className="recommandations-source-arrow">→</span>
                          </div>
                        </a>
                      </div>
                    </div>
                  );
                } else {
                  return (
                    <div key={sourceIndex} className={`recommandations-source-item recommandations-source-${source.type}`}>
                      <div className="recommandations-source-icon">🏛️</div>
                      <div className="recommandations-source-info">
                        <div className="recommandations-source-label">{source.label}</div>
                        <div className="recommandations-source-text">{source.text}</div>
                        <a 
                          href={source.url} 
                          target="_blank" 
                          rel="noopener noreferrer" 
                          className="recommandations-source-link"
                        >
                          Consulter la source
                          <span className="recommandations-source-arrow">→</span>
                        </a>
                      </div>
                    </div>
                  );
                }
              })}
            </div>
          </div>
        );
      
      default:
        return null;
    }
  };

  return (
    <div className="recommandations-page">
      <Navigation />
      
      <SEO 
        title="Conseils pour celui qui est éprouvé"
        description="Recommandations de Cheykh Souleymâne El-Rouheylî pour celui touché par la sorcellerie, le mauvais œil ou la maladie. Conseils islamiques authentiques basés sur le Coran et la Sunnah."
        url="https://mahi-bayan.netlify.app/recommandations-epreuve"
      />
      
      <div className="recommandations-content">
        <Header 
          mainTitle={["CONSEILS POUR CELUI QUI EST ÉPROUVÉ", "PAR LA SORCELLERIE, L'ŒIL OU LA MALADIE"]}
          subtitle="Cheykh Souleymâne El-Rouheylî"
        />

        <main className="recommandations-main">
          <div className="recommandations-content-wrapper">
            {data.content.map((item, index) => renderContentItem(item, index))}
          </div>
        </main>

        <Footer texts={[]} />
      </div>
    </div>
  );
};

export default RecommandationsPage;